import { Link } from 'react-router';
import { parseMenu, type FieldValues } from '@stitch/pbx-adapter/catalog';
import { duration, joinNames, lookup, summarize, type RoutingAccount } from '@stitch/pbx-adapter/describe';
import type { Directory } from '../api/client';
import { Icon, type IconName } from '../lib/icons';

/** How deep sub-menus are drawn inside one another. */
const MAX_DEPTH = 3;

const PAGE: Partial<Record<RoutingAccount['kind'], string>> = {
  ringGroup: 'ring-groups',
  callQueue: 'queues',
  autoAttendant: 'auto-attendants',
  timeRule: 'time-rules',
  callPark: 'call-park'
};

interface Target {
  tone: 'menu' | 'group' | 'queue' | 'person' | 'voicemail' | 'external' | 'warn' | 'other';
  icon: IconName;
  tag: string;
  name: string;
  detail?: string;
  /** A sub-menu, drawn inside. */
  menu?: RoutingAccount;
  /** Already on the way here: drawn as "back to", not again. */
  loop?: boolean;
  link?: string;
}

function targetOf(value: string, accounts: readonly RoutingAccount[], dir: Directory | undefined, path: readonly string[], base: string | undefined): Target {
  const v = value.trim();
  if (!v) return { tone: 'warn', icon: 'alert', tag: 'Not set', name: 'Nowhere yet' };
  const info = lookup(dir, v);
  const acc = accounts.find((a) => a.extension === v);
  const link = acc && base && PAGE[acc.kind] ? `${base}/${PAGE[acc.kind]}` : undefined;
  switch (acc?.kind ?? info.kind) {
    case 'autoAttendant':
      return { tone: 'menu', icon: 'flow', tag: 'Sub-menu', name: acc?.values.name || info.name, ...(acc ? { menu: acc } : {}), loop: path.includes(v), ...(link ? { link } : {}) };
    case 'ringGroup':
      return { tone: 'group', icon: 'bell', tag: 'Ring group', name: acc?.values.name || info.name, detail: acc ? summarize('ringGroup', acc.values, dir)[0] : undefined, ...(link ? { link } : {}) };
    case 'callQueue': {
      const agents = (acc?.values.agents ?? '').split(/\s+/).filter(Boolean);
      return {
        tone: 'queue',
        icon: 'headset',
        tag: 'Call queue',
        name: acc?.values.name || info.name,
        detail: acc ? (agents.length ? `${agents.length === 1 ? '1 agent' : `${agents.length} agents`}: ${joinNames(agents.map((a) => lookup(dir, a).name), 3)}` : 'No agents yet') : undefined,
        ...(link ? { link } : {})
      };
    }
    case 'person':
      return { tone: 'person', icon: 'users', tag: 'Person', name: info.name, detail: `Rings ext ${v}` };
    case 'voicemail':
      return { tone: 'voicemail', icon: 'mail', tag: 'Voicemail', name: info.name.replace(/'s voicemail$/, '') };
    case 'mailbox':
      return { tone: 'voicemail', icon: 'mail', tag: 'Voicemail box', name: info.name, detail: `Takes a message (box ${v})`, ...(base ? { link: `${base}/voicemail` } : {}) };
    case 'external':
      return { tone: 'external', icon: 'phone', tag: 'Phone number', name: v };
    case 'callPark':
      return { tone: 'other', icon: 'park', tag: 'Park orbit', name: acc?.values.name || info.name };
    case 'timeRule':
      return { tone: 'warn', icon: 'alert', tag: 'Time of day rule', name: acc?.values.name || info.name, detail: 'Calling a rule switches it; it doesn’t ring anyone.' };
    case 'other':
      return { tone: 'other', icon: 'grid', tag: 'Other', name: info.name };
    default:
      return { tone: 'warn', icon: 'alert', tag: 'Not in your account', name: v, detail: 'Calls sent here go nowhere.' };
  }
}

/**
 * How calls move through an auto attendant: greeting, each key, sub-menus drawn inside (up to a
 * few levels, loops shown as "back to"), what happens with no key, and the after-hours detour.
 * Works for a menu being edited (draft values) as well as saved ones.
 */
export function CallFlow({
  root,
  accounts,
  dir,
  base,
  depth = 0,
  path = []
}: {
  root: RoutingAccount;
  accounts: readonly RoutingAccount[];
  dir: Directory | undefined;
  /** Tenant portal base path, for links to the features calls go to. */
  base?: string;
  depth?: number;
  path?: readonly string[];
}) {
  const v: FieldValues = root.values;
  const here = [...path, root.extension];
  const options = parseMenu(v.menu ?? '').filter((o) => o.key);
  const rule = v.afterHoursRule ? accounts.find((a) => a.extension === v.afterHoursRule) : undefined;
  const ruleName = rule?.values.name || (v.afterHoursRule ? lookup(dir, v.afterHoursRule).name : '');

  const branch = (key: string, keyTone: string, label: string, value: string, caption?: string) => {
    const t = targetOf(value, accounts, dir, here, base);
    return (
      <li className="flow-branch" key={`${keyTone}-${key}-${label}`}>
        <span className={`flow-key ${keyTone}`} aria-label={label} title={label}>
          {keyTone === 'wait' ? <Icon name="clock" size={14} /> : keyTone === 'night' ? <Icon name="clock" size={14} /> : key}
        </span>
        <div className="flow-target">
          {caption && <span className="flow-caption">{caption}</span>}
          <div className={`flow-card ${t.tone}`}>
            <span className="flow-tag">
              <Icon name={t.icon} size={13} /> {t.tag}
            </span>
            <span className="flow-name">
              {t.loop ? (
                <>
                  <Icon name="undo" size={14} /> Back to {t.name}
                </>
              ) : t.link ? (
                <Link to={t.link}>{t.name}</Link>
              ) : (
                t.name
              )}
            </span>
            {t.detail && !t.loop && <span className="flow-detail">{t.detail}</span>}
          </div>
          {t.menu && !t.loop && depth < MAX_DEPTH && (
            <div className="flow-sub">
              <CallFlow root={t.menu} accounts={accounts} dir={dir} base={base} depth={depth + 1} path={here} />
            </div>
          )}
          {t.menu && !t.loop && depth >= MAX_DEPTH && <span className="flow-detail">…and its own menu</span>}
        </div>
      </li>
    );
  };

  return (
    <div className={`flow${depth ? ' nested' : ''}`} aria-label={depth ? `Sub-menu ${v.name || root.extension}` : `Call flow for ${v.name || root.extension}`}>
      {depth === 0 && (
        <div className="flow-start">
          <Icon name="phone" size={15} /> Calls to <strong>{v.name || 'this menu'}</strong>
          {root.extension && <span className="mono muted">{root.extension}</span>}
        </div>
      )}
      {v.afterHoursRule && v.afterHoursDestination && (
        <>
          <ul className="flow-branches">{branch('', 'night', `While "${ruleName}" is on`, v.afterHoursDestination, `While “${ruleName}” is on, calls skip the menu and go to`)}</ul>
          <span className="flow-when">Otherwise:</span>
        </>
      )}
      <div className="flow-greeting">
        <Icon name={v.greetingRecording ? 'mic' : 'bell'} size={14} />
        {v.greetingRecording ? (
          <span>Plays the recorded greeting</span>
        ) : v.greeting ? (
          <span className="flow-quote">“{v.greeting.length > 140 ? `${v.greeting.slice(0, 140)}…` : v.greeting}”</span>
        ) : (
          <span className="muted">No greeting yet</span>
        )}
      </div>
      <ul className="flow-branches">
        {options.map((o) => branch(o.key, 'key', `Press ${o.key}`, o.destination))}
        {v.dialByName && (
          <li className="flow-branch" key="name">
            <span className="flow-key key" aria-label={`Press ${v.dialByName}`}>
              {v.dialByName}
            </span>
            <div className="flow-target">
              <div className="flow-card other">
                <span className="flow-tag">
                  <Icon name="search" size={13} /> Directory
                </span>
                <span className="flow-name">Find someone by name</span>
              </div>
            </div>
          </li>
        )}
        {depth === 0 && (
          <li className="flow-branch" key="dial">
            <span className="flow-key any" aria-label="Dial an extension">
              ext
            </span>
            <div className="flow-target">
              <span className="flow-plain">Callers who know an extension can dial it at any time.</span>
            </div>
          </li>
        )}
        {v.timeoutDestination
          ? branch(
              '',
              'wait',
              `No key pressed${v.timeoutSeconds ? ` within ${duration(v.timeoutSeconds)}` : ''}`,
              v.timeoutDestination,
              `If nobody presses a key${v.timeoutSeconds ? ` within ${duration(v.timeoutSeconds)}` : ''}`
            )
          : depth === 0 && (
              <li className="flow-branch" key="nokey">
                <span className="flow-key wait" aria-label="No key pressed" title="No key pressed">
                  <Icon name="clock" size={14} />
                </span>
                <div className="flow-target">
                  <span className="flow-plain">If nobody presses a key: not set up yet.</span>
                </div>
              </li>
            )}
      </ul>

    </div>
  );
}
